import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import QuestionCard from '../components/QuestionCard';
import ProgressBar from '../components/ProgressBar';
import SectionStepper from '../components/SectionStepper';
import SectionProgressBar from '../components/SectionProgressBar';
import SectionReviewOverlay from '../components/SectionReviewOverlay';
import UnansweredSummary from '../components/UnansweredSummary';
import FullReviewAccordion from '../components/FullReviewAccordion';
import { QUESTIONS, CATEGORIES } from '../data/questions';
import { useQuizProgress } from '../hooks/useQuizProgress';
import { getSessionStatus, submitAnswers, ApiError } from '../lib/api';
import { computeSections, getSectionForIndex, getFirstUnansweredInSection } from '../lib/categoryUtils';
import { safeLocalStorage } from '../lib/safeStorage';
import type { Question, Category, Anatomy, AnswerValue } from '../types';

const ORDERED_CATEGORIES = CATEGORIES.map((c) => c.id) as Category[];
const CATEGORY_META_BY_ID = Object.fromEntries(
  CATEGORIES.map((category) => [category.id, category]),
) as Record<Category, (typeof CATEGORIES)[number]>;

// ── Per-partner anatomy filtering ─────────────────────────────────

function getQuestionsForPartner(
  questions: Question[],
  myAnatomy: Anatomy,
  partnerAnatomy: Anatomy,
): Question[] {
  return questions.filter((q) => {
    switch (q.visibility) {
      case 'always':             return true;
      case 'user-has-penis':     return myAnatomy === 'penis';
      case 'user-has-vulva':     return myAnatomy === 'vulva';
      case 'partner-has-penis':  return partnerAnatomy === 'penis';
      case 'partner-has-vulva':  return partnerAnatomy === 'vulva';
    }
  });
}

// ── Component ──────────────────────────────────────────────────────

export default function QuizPage() {
  const { sessionId = '', partner = 'A' } = useParams<{
    sessionId: string;
    partner: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── Token handling ─────────────────────────────────────────────────
  // Partner B arrives with ?token= in the URL. Save it and clean the URL.
  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      safeLocalStorage.setItem(`quiz_token_${sessionId}`, tokenFromUrl);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, sessionId, setSearchParams]);

  const partnerToken = safeLocalStorage.getItem(`quiz_token_${sessionId}`) ?? '';

  // ── Session metadata ───────────────────────────────────────────────
  const [sessionMeta, setSessionMeta] = useState<{
    partnerAName: string;
    partnerBName: string;
    partnerAAnatomy: Anatomy;
    partnerBAnatomy: Anatomy;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchSession() {
      try {
        const data = await getSessionStatus(sessionId);

        // If this partner already submitted, redirect to waiting
        const alreadyDone =
          (partner === 'A' && data.partnerACompleted) ||
          (partner === 'B' && data.partnerBCompleted);
        if (alreadyDone) {
          navigate(`/waiting/${sessionId}/${partner}`, { replace: true });
          return;
        }

        setSessionMeta({
          partnerAName: data.partnerAName,
          partnerBName: data.partnerBName,
          partnerAAnatomy: data.partnerAAnatomy as Anatomy,
          partnerBAnatomy: data.partnerBAnatomy as Anatomy,
        });
      } catch (err) {
        if (err instanceof ApiError) {
          setLoadError(err.status === 404 ? 'Session not found or expired.' : err.message);
        } else {
          setLoadError('Failed to load session. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    }

    if (sessionId) fetchSession();
  }, [sessionId, partner, navigate]);

  // ── Quiz progress ──────────────────────────────────────────────────
  const { answers, setAnswer } = useQuizProgress(sessionId, partner);

  const filteredQuestions = useMemo(() => {
    if (!sessionMeta) return [];
    const myAnatomy = partner === 'A' ? sessionMeta.partnerAAnatomy : sessionMeta.partnerBAnatomy;
    const theirAnatomy = partner === 'A' ? sessionMeta.partnerBAnatomy : sessionMeta.partnerAAnatomy;
    return getQuestionsForPartner(QUESTIONS, myAnatomy, theirAnatomy);
  }, [sessionMeta, partner]);

  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [completedSectionCategory, setCompletedSectionCategory] = useState<Category | null>(null);
  const [showReviewOverlay, setShowReviewOverlay] = useState(false);
  const [showFullReview, setShowFullReview] = useState(false);

  // Initialize current index once questions are loaded
  useEffect(() => {
    if (filteredQuestions.length > 0 && currentIndex === null) {
      const firstUnanswered = filteredQuestions.findIndex((q) => !(q.id in answers));
      if (firstUnanswered === -1 && Object.keys(answers).length > 0) {
        setShowSummary(true);
        setCurrentIndex(filteredQuestions.length - 1);
      } else {
        setCurrentIndex(firstUnanswered === -1 ? 0 : firstUnanswered);
      }
    }
  }, [filteredQuestions, answers, currentIndex]);

  const totalQuestions = filteredQuestions.length;

  // Category stats
  const { answeredCount, totalByCategory, answeredByCategory } = useMemo(() => {
    const totalMap = {} as Record<Category, number>;
    const answeredMap = {} as Record<Category, number>;

    for (const cat of ORDERED_CATEGORIES) {
      totalMap[cat] = 0;
      answeredMap[cat] = 0;
    }

    let answered = 0;
    for (const q of filteredQuestions) {
      totalMap[q.category]++;
      if (q.id in answers) {
        answeredMap[q.category]++;
        answered++;
      }
    }

    return {
      answeredCount: answered,
      totalByCategory: totalMap,
      answeredByCategory: answeredMap,
    };
  }, [filteredQuestions, answers]);

  const activeCategories = useMemo(
    () => ORDERED_CATEGORIES.filter((cat) => totalByCategory[cat] > 0),
    [totalByCategory],
  );

  const safeIndex = currentIndex ?? 0;
  const currentQuestion: Question | undefined = filteredQuestions[safeIndex];
  const currentCategory = currentQuestion?.category;

  // ── Section-based navigation ─────────────────────────────────────
  const sections = useMemo(
    () => computeSections(filteredQuestions, activeCategories),
    [filteredQuestions, activeCategories],
  );

  const currentSection = useMemo(
    () => getSectionForIndex(sections, safeIndex),
    [sections, safeIndex],
  );

  const sectionQuestionNumber = useMemo(() => {
    if (!currentSection) return 1;
    return safeIndex - currentSection.startIndex + 1;
  }, [currentSection, safeIndex]);

  const nextSectionMeta = useMemo(() => {
    if (!completedSectionCategory) return null;
    const idx = sections.findIndex((s) => s.category === completedSectionCategory);
    if (idx === -1 || idx + 1 >= sections.length) return null;
    const next = sections[idx + 1];
    if (!next) return null;
    const meta = CATEGORY_META_BY_ID[next.category];
    return meta ? { ...meta, questionCount: next.questions.length } : null;
  }, [completedSectionCategory, sections]);

  const completedSectionMeta = useMemo(() => {
    if (!completedSectionCategory) return null;
    return CATEGORY_META_BY_ID[completedSectionCategory] ?? null;
  }, [completedSectionCategory]);

  const completedSectionIndex = useMemo(() => {
    if (!completedSectionCategory) return 0;
    return sections.findIndex((s) => s.category === completedSectionCategory) + 1;
  }, [completedSectionCategory, sections]);

  // ── Navigation handlers ──────────────────────────────────────────

  function handleAnswer(questionId: string, value: AnswerValue) {
    setAnswer(questionId, value);
    moveNext(questionId);
  }

  function handleListAnswer(questionId: string, value: AnswerValue) {
    setAnswer(questionId, value);
  }

  function moveNext(justAnsweredId?: string) {
    if (safeIndex + 1 >= totalQuestions) {
      setShowSummary(true);
      return;
    }

    const nextIndex = safeIndex + 1;
    const nextQuestion = filteredQuestions[nextIndex];

    // Check if we're crossing a section boundary
    if (currentSection && nextQuestion && nextQuestion.category !== currentSection.category) {
      // Check if every question in the current section is answered
      const allAnswered = currentSection.questions.every(
        (q) => q.id in answers || q.id === justAnsweredId,
      );
      if (allAnswered) {
        setCompletedSectionCategory(currentSection.category);
        setShowTransition(true);
        setCurrentIndex(nextIndex);
        return;
      }
    }

    setCurrentIndex(nextIndex);
  }

  function handlePrev() {
    if (safeIndex > 0) {
      setCurrentIndex(safeIndex - 1);
      setShowSummary(false);
    }
  }

  function handleNext() {
    moveNext();
  }

  function handleSectionJump(category: Category) {
    const section = sections.find((s) => s.category === category);
    if (!section) return;
    const targetIndex = getFirstUnansweredInSection(section, answers);
    setCurrentIndex(targetIndex);
    setShowSummary(false);
    setShowTransition(false);
    setShowReviewOverlay(false);
  }

  function handleTransitionContinue() {
    setShowTransition(false);
    setCompletedSectionCategory(null);
    setShowReviewOverlay(false);
  }

  function handleTransitionReview() {
    if (completedSectionCategory) {
      const section = sections.find((s) => s.category === completedSectionCategory);
      if (section) setCurrentIndex(section.startIndex);
    }
    setShowTransition(false);
    setCompletedSectionCategory(null);
    setShowReviewOverlay(false);
  }

  async function handleSubmit() {
    if (!partnerToken) {
      setSubmitError('No partner token found. Please use the share link you were given.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await submitAnswers({
        sessionId,
        partnerToken,
        answers,
      });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else {
        setSubmitError('Failed to submit answers. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // ── Loading state ──────────────────────────────────────────────────

  if (isLoading) {
    return (
      <AppShell showBack backTo="/" title="Quiz">
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="text-4xl mb-4 animate-pulse">📝</div>
          <p className="text-slate-400">Loading your quiz...</p>
        </div>
      </AppShell>
    );
  }

  // ── Error state ────────────────────────────────────────────────────

  if (loadError) {
    return (
      <AppShell showBack backTo="/" title="Quiz">
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="text-5xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-white mb-3">
            {loadError}
          </h1>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-6 py-3 gradient-cta text-white font-semibold rounded-xl transition-all"
          >
            Go home
          </button>
        </div>
      </AppShell>
    );
  }

  // ── No token warning ───────────────────────────────────────────────

  if (!partnerToken) {
    return (
      <AppShell showBack backTo="/" title="Quiz">
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="text-5xl mb-4">🔑</div>
          <h1 className="text-2xl font-bold text-white mb-3">
            Missing access token
          </h1>
          <p className="text-slate-400 mb-6">
            Open this page using the share link you were given. If you started the quiz in a
            different browser, please use that browser instead.
          </p>
        </div>
      </AppShell>
    );
  }

  // ── Submitted state ────────────────────────────────────────────────

  if (submitted) {
    const otherName = partner === 'A' ? sessionMeta?.partnerBName : sessionMeta?.partnerAName;
    return (
      <AppShell showBack backTo="/" title="Quiz">
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="text-6xl mb-6">✅</div>
          <h1 className="text-2xl font-bold text-white mb-3">
            Answers submitted!
          </h1>
          <p className="text-slate-400 mb-8">
            Waiting for {otherName ?? 'your partner'} to finish their quiz...
          </p>
          <button
            onClick={() => navigate(`/waiting/${sessionId}/${partner}`)}
            className="px-6 py-3 gradient-cta text-white font-semibold rounded-xl transition-all"
          >
            Go to Waiting Room
          </button>
        </div>
      </AppShell>
    );
  }

  // ── Summary before submit ────────────────────────────────────────

  if (showSummary) {
    const skippedCount = totalQuestions - answeredCount;

    // Full review accordion mode
    if (showFullReview) {
      return (
        <AppShell showBack backTo="/" title="Quiz">
          <div className="max-w-lg mx-auto px-4 py-6">
            <FullReviewAccordion
              sections={sections}
              answers={answers}
              onAnswer={handleListAnswer}
              onBackToQuiz={() => {
                setShowFullReview(false);
                setShowSummary(false);
              }}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              submitError={submitError}
            />
          </div>
        </AppShell>
      );
    }

    return (
      <AppShell showBack backTo="/" title="Quiz">
        <div className="max-w-lg mx-auto px-4 py-8">
          <div className="mb-8">
            <ProgressBar
              categories={activeCategories}
              answeredByCategory={answeredByCategory}
              totalByCategory={totalByCategory}
            />
          </div>

          <div className="bg-white rounded-2xl shadow-xl border-2 border-accent/20 p-6 sm:p-8 text-center">
            <div className="text-5xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              You've reviewed all questions!
            </h2>
            <p className="text-slate-500 mb-6">
              You answered <span className="font-semibold text-slate-900">{answeredCount}</span> of{' '}
              <span className="font-semibold text-slate-900">{totalQuestions}</span> questions
              {skippedCount > 0 && (
                <>
                  {' '}
                  (<span className="text-amber-600">{skippedCount} skipped</span>)
                </>
              )}
            </p>

            {skippedCount > 0 && (
              <p className="text-sm text-slate-500 mb-6">
                Skipped questions won't count as matches. You can go back and answer them.
              </p>
            )}

            {submitError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm text-red-600">{submitError}</p>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full px-6 py-3 gradient-cta disabled:opacity-50 text-white font-semibold rounded-xl transition-all"
              >
                {isSubmitting ? 'Submitting...' : 'Submit my answers'}
              </button>
              <button
                onClick={() => setShowFullReview(true)}
                className="w-full px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors"
              >
                Review my answers
              </button>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-200">
              <UnansweredSummary
                sections={sections}
                answers={answers}
                onJumpToQuestion={(globalIndex) => {
                  setCurrentIndex(globalIndex);
                  setShowSummary(false);
                }}
              />
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  // ── Quiz card view ───────────────────────────────────────────────

  return (
    <AppShell showBack backTo="/" title="Quiz">
      <div className="max-w-lg mx-auto px-4 py-6 sm:py-8">
        {/* Consolidated progress block */}
        <div className="mb-6">
          {/* Category icon stepper */}
          <SectionStepper
            categories={activeCategories}
            currentCategory={currentCategory ?? activeCategories[0] ?? 'basics'}
            answeredByCategory={answeredByCategory}
            totalByCategory={totalByCategory}
            onSectionClick={handleSectionJump}
          />

          {/* Section label + question counter */}
          {currentCategory && currentSection && (
            <div className="flex justify-between items-end mt-2 mb-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-primary">
                Section {activeCategories.indexOf(currentCategory) + 1} of {activeCategories.length} · {CATEGORY_META_BY_ID[currentCategory]?.label}
              </span>
              <span className="text-[10px] font-bold text-slate-400">
                {sectionQuestionNumber} / {currentSection.questions.length}
              </span>
            </div>
          )}

          {/* Gradient progress bar */}
          {currentCategory && (
            <SectionProgressBar
              answered={answeredByCategory[currentCategory] || 0}
              total={totalByCategory[currentCategory] || 0}
            />
          )}
        </div>

        {showTransition && completedSectionMeta ? (
          /* ── Section transition card ─────────────────────────────── */
          <div className="bg-white rounded-2xl shadow-xl border-2 border-accent/20 p-6 sm:p-8 text-center">
            <div className="text-4xl mb-2">{completedSectionMeta.emoji}</div>
            <div className="text-2xl mb-4">✅</div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">
              {completedSectionMeta.label} complete!
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              Section {completedSectionIndex} of {sections.length} done
            </p>

            {nextSectionMeta ? (
              <p className="text-slate-500 mb-6">
                Next up: {nextSectionMeta.emoji} {nextSectionMeta.label} ({nextSectionMeta.questionCount} questions)
              </p>
            ) : (
              <p className="text-slate-500 mb-6">
                You've completed all sections!
              </p>
            )}

            <div className="flex flex-col gap-3">
              <button
                onClick={handleTransitionContinue}
                className="w-full px-6 py-3 gradient-cta text-white font-semibold rounded-xl transition-all"
              >
                {nextSectionMeta ? 'Continue' : 'Review & Submit'} &rarr;
              </button>
              <button
                onClick={handleTransitionReview}
                className="text-sm text-slate-400 hover:text-slate-700 transition-colors"
              >
                Review this section
              </button>
            </div>
          </div>
        ) : (
          /* ── Single question card ────────────────────────────────── */
          <>
            {currentQuestion && (
              <QuestionCard
                key={currentQuestion.id}
                question={currentQuestion}
                currentAnswer={answers[currentQuestion.id] ?? null}
                onAnswer={handleAnswer}
                partnerName={
                  (partner === 'A' ? sessionMeta?.partnerBName : sessionMeta?.partnerAName) ??
                  'your partner'
                }
              />
            )}

            <div className="flex justify-between mt-6">
              <button
                onClick={handlePrev}
                disabled={safeIndex === 0}
                className="px-4 py-2 text-sm font-medium text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                &larr; Previous
              </button>
              <button
                onClick={handleNext}
                disabled={safeIndex + 1 >= totalQuestions}
                className="px-4 py-2 text-sm font-medium text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                {currentQuestion && currentQuestion.id in answers ? 'Next' : 'Skip'} &rarr;
              </button>
            </div>
          </>
        )}
      </div>

      {/* Section review overlay */}
      {currentSection && (
        <SectionReviewOverlay
          isOpen={showReviewOverlay}
          onClose={() => {
            setShowReviewOverlay(false);
            const firstUnanswered = getFirstUnansweredInSection(currentSection, answers);
            setCurrentIndex(firstUnanswered);
          }}
          questions={currentSection.questions}
          answers={answers}
          onAnswer={handleListAnswer}
          sectionLabel={CATEGORY_META_BY_ID[currentSection.category]?.label ?? ''}
          sectionEmoji={CATEGORY_META_BY_ID[currentSection.category]?.emoji ?? ''}
        />
      )}
    </AppShell>
  );
}
